<template>
  <div class="pt-6 hyp-content-details-container">
    <div v-if="!isMinimized && hasMaritimeAccess">
      <Loader
        v-if="!details"
        :text="loaderText"
        size="small"
        class="mt-5"
      ></Loader>

      <div v-if="details" class="details-container">
        <v-row>
          <v-col cols="6" class="pt-0 col col-6 pl-6">
            <h1>
              <v-icon
                v-if="!isMinimized"
                size="18"
                @click="handleClickMinimize"
                title="Back to Map"
                class="mr-3"
                >fas fa-arrow-left</v-icon
              >
              {{ selectedLocation.name }}
            </h1>
          </v-col>
          <v-col cols="6" class="pt-0 pr-6 col col-6 text-right">
            <RisksReport
              class="pl-2 pull-right"
              v-if="selectedLocation"
              :selected-location="selectedLocation"
            ></RisksReport>
          </v-col>
        </v-row>

        <v-container class="hyp-risk-details-container">
          <v-row>
            <v-col cols="12" class="pt-0">
              <!-- Risks -->
              <v-container class="hyp-risk-tab-items mt-3">
                <v-row id="hyp-risk-grid">
                  <v-col
                    id="hyp-risk-content-wrapper"
                    cols="12"
                    class="pt-0 content-container"
                  >
                    <RisksContentContainerWrapper
                      class="hyp-risk-grid-2-left"
                      :wrapper-class="'mt-3 mr-3'"
                      wrapper-id="risks-headlines"
                      expand-btn-title="View in Full Page"
                      @refresh-content="refreshRiskWaterHeadlineContent"
                      :expand="refresh"
                      :reset-expanded="resetExpanded"
                    >
                      <RisksHeadlines
                        :selected-location="selectedLocation"
                        :params="waterRiskparams"
                        @click-close="handleClickClose"
                        @click-minimize="handleClickMinimize"
                        @selected-tab="handleTabChange"
                        @selected-dates="handleDatesChange"
                        @selected-dates-by-user="handleDatesByUserChange"
                        @selected-sort="handleSortChange"
                        :selected-tab-index="selectedTabIndex"
                        :selected-tab-content="selectedTabContent"
                        :selected-tab-dates="selectedTabDates"
                        :selected-tab-dates-by-user="selectedTabDatesByUser"
                        :selected-sort="selectedSort"
                        :headline-params="params"
                        :key="refresh"
                      ></RisksHeadlines>
                    </RisksContentContainerWrapper>
                  </v-col>
                  <v-col>
                    <RisksPulseRatings
                      class="mb-2 expand-left"
                      v-if="selectedLocation"
                      :selected-location="selectedLocation"
                      :risksContentExpanded="risksPulseRatingsExpanded"
                      :params="params"
                      :selected-primary-risk-category="
                        selectedPrimaryRiskCategory
                      "
                    ></RisksPulseRatings>

                    <RisksContentContainerWrapper
                      class="mb-2 pa-3 hyp-risk-content-body expand-left"
                      wrapper-id="risks-profile-body"
                      expand-btn-title="View in Full Page"
                      :reset-expanded="resetExpanded"
                      @click-expand="handleClickExpand"
                    >
                      <RisksProfileBody
                        v-if="profileInfo"
                        :profile-info="profileInfo"
                        :risks-content-expanded="risksProfileBodyExpanded"
                      ></RisksProfileBody>
                    </RisksContentContainerWrapper>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </div>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";

import { RISK_RATING_PRIMARIES } from "@/common/constants/controlRisks/risks";
import {
  getWaterDetails,
  getRiskRatingsByWaterCode,
} from "@/services/controlRisks/risks.service";
import { hasMaritimeAccess } from "@/services/user/user.service";

const EMITTED_ACTIONS = {
  CLICK_CLOSE: "click-close",
  CLICK_MINIMIZE: "click-minimize",
};

export default {
  name: "RisksWaterContent",
  components: {
    Loader: () => import("@/components/common/Loader"),
    RisksReport: () => import("@/components/controlRisks/risks/RisksReport"),
    RisksContentContainerWrapper: () =>
      import("@/components/controlRisks/risks/RisksContentContainerWrapper"),
    RisksPulseRatings: () =>
      import("@/components/controlRisks/risks/RisksPulseRatings"),
    RisksProfileBody: () =>
      import("@/components/controlRisks/risks/RisksProfileBody"),
    RisksHeadlines: () =>
      import("@/components/controlRisks/risks/RisksHeadlines"),
  },
  props: {
    selectedLocation: {
      type: Object,
    },
    isMinimized: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object,
      default: () => {
        return { hidePulse: true };
      },
    },
  },
  data() {
    return {
      resetExpanded: new Date().toISOString(),
      details: null,
      ratings: null,
      risksPulseRatingsExpanded: null,
      risksProfileBodyExpanded: null,
      profileInfo: null,
      refresh: 0,
      selectedTabIndex: 0,
      selectedTabContent: null,
      selectedTabDates: {},
      selectedTabDatesByUser: false,
      selectedSort: null,
      hasMaritimeAccess: hasMaritimeAccess(),
    };
  },
  computed: {
    selectedPrimaryRiskCategory() {
      return lodashFind(this.ratings, [
        "riskCategoryId",
        RISK_RATING_PRIMARIES.maritime,
      ]);
    },
    loaderText() {
      const selectedText =
        this.selectedLocation && this.selectedLocation.name
          ? this.selectedLocation.name
          : "";
      return `Loading ${selectedText} details`;
    },
    waterRiskparams() {
      return {
        aoiId: this.selectedLocation.code,
      };
    },
  },
  methods: {
    handleClickClose() {
      this.$emit(EMITTED_ACTIONS.CLICK_CLOSE);
    },
    handleClickMinimize() {
      this.$emit(EMITTED_ACTIONS.CLICK_MINIMIZE);
    },
    refreshRiskWaterHeadlineContent(expand) {
      this.refresh = expand;
    },
    handleTabChange(selectedTab, filter) {
      this.selectedTabIndex = selectedTab;
      this.selectedTabContent = filter;
    },
    handleDatesChange(dates) {
      this.selectedTabDates = dates;
    },
    handleDatesByUserChange(selectedTabDatesByUser) {
      this.selectedTabDatesByUser = selectedTabDatesByUser;
    },
    handleSortChange(sort) {
      this.selectedSort = sort;
    },
    handleClickExpand(wrapperId, expandedYN) {
      this.risksPulseRatingsExpanded = null;
      this.risksProfileBodyExpanded = null;
      if (wrapperId === "risks-pulse-ratings") {
        this.risksPulseRatingsExpanded = expandedYN ? wrapperId : null;
      }
      if (wrapperId === "risks-profile-body") {
        this.risksProfileBodyExpanded = expandedYN ? wrapperId : null;
      }
    },
    async getContent() {
      this.details = await getWaterDetails(this.selectedLocation.code);
      if (this.details?.overview && this.details.overview.length > 0) {
        this.profileInfo = this.details.overview[0];
      }
      return true;
    },
    async getRatings() {
      const { waters } = await getRiskRatingsByWaterCode(
        this.selectedLocation.code
      );
      if (
        waters &&
        Array.isArray(waters) &&
        waters.length &&
        lodashGet(waters, "[0].properties.ratings")
      ) {
        this.ratings = waters[0].properties.ratings;
      }
      return true;
    },
  },
  watch: {
    selectedLocation: {
      async handler() {
        if (this.selectedLocation && this.selectedLocation.code) {
          await this.getContent();
          await this.getRatings();
          this.risksPulseRatingsExpanded = null;
          this.risksProfileBodyExpanded = null;
          this.resetExpanded = new Date().toISOString();
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style>
.content-container img {
  max-width: 460px;
}
</style>

<style scoped>
.hyp-risk-details-container {
  margin: 0;
  padding: 0;
  max-width: unset !important;
  line-height: 1.5;
}
.hyp-risk-tab-items {
  height: calc(100vh - 70px);
  overflow-y: auto;
  padding-top: 0;
  padding-bottom: 0;
}
#hyp-risk-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-width: 900px;
  overflow-x: auto;
  height: 100%;
}
#hyp-risk-grid > .col:first-child {
  margin-bottom: 8px;
}
#hyp-risk-grid > .col:first-child > * {
  height: 100%;
}
#hyp-risk-grid > .col:nth-child(2) {
  display: grid;
  grid-template-rows: min(25%, 195px) 1fr;
  padding-top: 0;
  padding-left: 0;
}
.details-heading {
  font-size: 20px;
}
</style>
